import { BeneficiaryDto } from '@alpha/bene-dtos';

export type TBeneficiaryApproved = BeneficiaryDto &
  TBeneficiaryPaymentMethodObj &
  Omit<TBeneficiaryPersonalDetails, 'addressLine1' | 'addressLine2'> & {
    address: string;
    accountBeneId: string;
  };

export type TBeneficiaryPending = BeneficiaryDto;

export type TBeneficiaryPersonalDetails = {
  name: string;
  friendlyName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string | undefined;
};

export type TPaymentTypes = 'local' | 'international';
export type TLocal = {
  accountNumber?: string;
  nationalBankCode?: string;
};
export type TInternational = {
  iban?: string;
  swift?: string;
  accountNumber?: string;
};

export type TBeneficiaryPaymentMethodObj = {
  iban: string;
  swift: string;
  accountNumber: string;
  nationalBankCode: string;
};
export type TInternationalOptions = 'iban' | 'swift' | 'accountNumber' | 'nationalBankCode';
export type TLocalOptions = 'accountNumber' | 'nationalBankCode';
export type TBeneficiaryPaymentMethods = TInternational & TLocal & {
  nationalBankCodeType?: string
};
export type TBeneficiaryBankDetails = {
  bankCountry: string;
  currency: string;
  bankInformation: TBeneficiaryPaymentMethods;
  correspondent: TBeneficiarycorrespondent;
  furtherTo: TBeneficiaryFurtherTo;
};

export type TBeneficiarycorrespondent = {
  correspondentAccountNumber?: string;
  correspondentSwift?: string;
};
export type TBeneficiaryFurtherTo = {
  furtherToAccountNumber?: string;
  furtherToSwift?: string;
};
export type TBeneficiaryResponse = {
  hasMore: boolean;
  nextStartFrom: string;
  items: BeneficiaryDto[];
};

export type TBeneBatchStatus = 'UPLOADED' | 'VALIDATED' | 'INVALID' | 'PROCESSED';
export type TBeneficiaryCreateRequest = {
  name: string;
  bankCountryCode: string;
  iban?: string;
  accountNumber?: string;
  swift?: string;
  nationalBankCode?: string;
  correspondentAccountNumber?: string;
  correspondentSwift?: string;
  emailAddress?: string;
  currencyCode: string;
} & TBeneficiaryPersonalDetails;

export type TBankingFieldsWithValidation = {
  international: TBankingFieldWithValidation[],
  local: TBankingFieldWithValidation[]
}

export type TBankingFieldWithValidation = {
  field: 'IBAN' | 'Account Number' | 'SWIFT' | 'National Bank Code',
  pattern: string,
  errorMessage: string
};

export type TBeneBatchId = string;
export type TBeneBankFieldsResponse = {
  accountNumberLabel?: string;
  nationalBankCodeLabel?: string;
} & TBankingFieldsWithValidation;

export type TBeneficiaryUploadResponse = {
  accountId: string;
  batchId: TBeneBatchId;
  batchStatus: TBeneBatchStatus;
  noOfBenes: number;
  noOfInvalidBenes: number;
  uploadedBy: string;
  uploadedDate: string;
};
export type TBeneficiaryInvalidItem = {
  accountId: string;
  accountNumber?: string;
  swift?: string;
  iban?: string;
  nationalBankCode?: string;
  applyFinancialValidation: unknown;
  batchId: TBeneBatchId;
  batchLineNumber: number;
  errors: string[];
  id: string;
  name: string;
  status: TBeneBatchStatus;
  applyFinancialErrored: boolean;
};
export type TBeneficiaryInvalidResponse = {
  hasMore: boolean;
  items: TBeneficiaryInvalidItem[];
  nextStartFrom: null | string;
};
export type TBeneficiaryStatusResponse = TBeneficiaryUploadResponse;


export type Place = {
  formattedAddress: string,
  addressComponents: {
    longText: string,
    shortText?: string,
    languageCode: string,
    types?: string[],
  },
  displayName: {
    text: string,
    languageCode: string,
  },
}

export type AddressComponent = {
  street_number?: Place['addressComponents'];
  street_address?: Place['addressComponents'];
  route?: Place['addressComponents'];
  intersection?: Place['addressComponents'];
  political?: Place['addressComponents'];
  administrative_area_level_1?: Place['addressComponents'];
  administrative_area_level_2?: Place['addressComponents'];
  administrative_area_level_3?: Place['addressComponents'];
  administrative_area_level_4?: Place['addressComponents'];
  administrative_area_level_5?: Place['addressComponents'];
  administrative_area_level_6?: Place['addressComponents'];
  administrative_area_level_7?: Place['addressComponents'];
  colloquial_area?: Place['addressComponents'];
  locality?: Place['addressComponents'];
  sublocality?: Place['addressComponents'];
  neighborhood?: Place['addressComponents'];
  premise?: Place['addressComponents'];
  subpremise?: Place['addressComponents'];
  postal_code?: Place['addressComponents'];
  postal_town?: Place['addressComponents'];
  country?: Place['addressComponents'];
};
export const AddressTypes = [
  'street_number', // line 1
  'street_address', // line 1
  'route',// line 1
  'premise', // line 1
  'subpremise', // line 1
  'sublocality', // line 2
  'neighborhood', // line 2
  'intersection', // line 2
  'administrative_area_level_1', // state / province
  'administrative_area_level_2', // state / province
  'administrative_area_level_3', // state / province
  'administrative_area_level_4', // state / province
  'administrative_area_level_5', // state / province
  'administrative_area_level_6', // state / province
  'administrative_area_level_7', // state / province
  'colloquial_area', // city
  'locality', // city
  'postal_town', // city
  'postal_code', // zip
  'country', // country
];